<template>
	<div
		class="company-element-card p-100 height-100 cursor-pointer"
		data-test="company-element-card"
		@mouseenter="isHovered = true"
		@mouseleave="isHovered = false"
		@click="handleClick"
	>
		<div class="display-flex align-items-center">
			<img :src="coverPhoto" :alt="`${companyElement.companyInfo.assetName} logo`" class="company-logo" />
			<h2 :class="['asset-name heading-5 text-color-content-primary', clampClass]">
				{{ companyElement.companyInfo.assetName }}
			</h2>
			<BaseSvgIcon
				v-if="!appStore.isDesktop || isHovered"
				name="chevron"
				dir="right"
				width="24"
				height="24"
				class="dark"
				style="margin-left: -0.5rem"
			/>
		</div>
		<RichTextComponent
			class="summary line-clamp-2 epsilon text-color-content-secondary my-100"
			:blocks="companyElement.companyInfo.summary"
		/>
		<div class="mb-150">
			<CompanyAssetsTag :company-asset-tag="assetTypeTag" />
			<CompanyAssetsTag :company-asset-tag="industryTag" />
		</div>
		<div class="valuation-at-last-funding display-flex justify-content-space-between pt-100">
			<span class="text-color-content-secondary">Valuation at Last Funding</span>
			<span class="font-weight-semi-bold dark">{{ lastFundingValuation }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { CompanyAssetTag, CompanyAssetTypeLabel, CompanyElement, InvestmentStatus } from 'types/company-element';
import { computed, ref } from 'vue';
import { Asset } from 'types/assets';
import CompanyAssetsTag from '@components/marketing-site/company-assets-tag.vue';
import { Industry } from 'types/asset-details';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { useAppStore } from '@stores/app';
import { useRouter } from 'vue-router';

interface Props {
	asset: Asset;
}

const props = defineProps<Props>();

const router = useRouter();

const appStore = useAppStore();

const isHovered = ref(false);

const companyElement = computed((): CompanyElement => {
	const { city, state } = props.asset.ventureCapitalFields ?? {};
	const location = city && state ? `${city}, ${state}` : '';

	return {
		id: props.asset.fundriseId,
		companyInfo: {
			assetName: props.asset.name,
			assetType: props.asset.assetType.name as CompanyAssetTypeLabel,
			coverPhoto: props.asset.thumbnail?.url ?? '',
			website: props.asset.ventureCapitalFields?.website ?? '',
			description: props.asset.summary ?? [],
			summary: props.asset.summary ?? [],
			industry: {
				abbreviation: props.asset.ventureCapitalFields?.industry?.abbreviation ?? ''
			} as Industry,
			headcount: props.asset.ventureCapitalFields?.headcount ?? '',
			yearFounded: props.asset.ventureCapitalFields?.yearFounded ?? '',
			location
		},
		assetFunding: {
			lastFundingValuation: props.asset.ventureCapitalFields?.lastFundingValuation ?? ''
		},
		fundriseInvestmentData: {
			investmentStatus: props.asset.status?.label as InvestmentStatus
		}
	};
});

const coverPhoto = computed((): string => companyElement.value.companyInfo.coverPhoto ?? '');

const lastFundingValuation = computed((): string => companyElement.value.assetFunding?.lastFundingValuation ?? '-');

const assetTypeTag = computed((): CompanyAssetTag => {
	return {
		assetType: 'assetType',
		label: companyElement.value.companyInfo.assetType
	};
});

const industryTag = computed((): CompanyAssetTag => {
	return {
		assetType: 'industry',
		label: companyElement.value.companyInfo.industry?.abbreviation ?? ''
	};
});

const clampClass = computed((): string => {
	const maxCharacters = 14;
	const assetNameStringTooLong =
		companyElement.value.companyInfo.assetName.split(' ').length === 1 &&
		companyElement.value.companyInfo.assetName.length >= maxCharacters;

	if (assetNameStringTooLong) {
		return 'ellipsis';
	}
	return 'line-clamp-2';
});

async function handleClick(): Promise<void> {
	await router
		.push({ name: 'company-assets', params: { companyId: companyElement.value.id } })

		.catch(() => {});
}
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors.scss' as *;

.company-element-card {
	background: #ffffff;
	border: 1px solid token('bg-tertiary');
	border-radius: 8px;
}

.asset-name {
	padding: 0 12px;
	flex: 0 0 calc(100% - 82px);
	&:hover {
		cursor: pointer;
	}
}

.company-logo {
	width: 70px;
	height: 70px;
	border-radius: 4px;
}

.summary {
	height: 48px;
}

.valuation-at-last-funding {
	border-top: 1px solid token('bg-tertiary');
}
</style>
